import { useCallback, useEffect, useRef, useState } from "react";
import { constrainPointToOrbit, type CalibratedPoint } from "../shared/tracking.ts";
import { playablePoint } from "../shared/premium-game.ts";
import { beginActivity, finishActivity, syncAccountActivity, type ActiveActivity } from "./activity.ts";
import { trackEvent } from "./analytics.tsx";
import type { Session } from "./Shell.tsx";
import { useHeadInput } from "./useHeadInput.ts";

export type GentleGameStage = "intro" | "starting" | "calibrating" | "countdown" | "playing" | "complete";
export type GentleControlMode = "camera" | "manual";

export function useGentleGame(game: string, session: Session | null, { calibrateComfort = false }: { calibrateComfort?: boolean } = {}) {
  const tracker = useHeadInput({ calibrateComfort });
  const [stage, setStage] = useState<GentleGameStage>("intro");
  const [mode, setMode] = useState<GentleControlMode>("camera");
  const [countdown, setCountdown] = useState(3);
  const [manualPoint, setManualPoint] = useState<CalibratedPoint>({ x: 0, y: 0 });
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(0);
  const activity = useRef<ActiveActivity | null>(null);
  const permissionTracked = useRef(false);

  const saveActivity = useCallback((outcome: "completed" | "stopped") => {
    if (!activity.current) return;
    finishActivity(activity.current, outcome);
    activity.current = null;
    if (session) void syncAccountActivity().catch(() => {});
  }, [session]);

  const reset = useCallback(() => {
    saveActivity("stopped");
    tracker.stop();
    setStage("intro");
    setCountdown(3);
    setElapsed(0);
    setManualPoint({ x: 0, y: 0 });
  }, [saveActivity, tracker.stop]);

  const startCamera = useCallback(async () => {
    setMode("camera");
    setStage("starting");
    setCountdown(3);
    permissionTracked.current = false;
    await tracker.start();
  }, [tracker.start]);

  const startManual = useCallback(() => {
    tracker.stop();
    setMode("manual");
    setManualPoint({ x: 0, y: 0 });
    setCountdown(3);
    setStage("countdown");
  }, [tracker.stop]);

  const complete = useCallback(() => {
    const seconds = Math.max(1, Math.round((performance.now() - startedAt.current) / 1_000));
    saveActivity("completed");
    setElapsed(seconds);
    setStage("complete");
    tracker.stop();
    void trackEvent("game_completed", { game, mode, duration_seconds: seconds });
  }, [game, mode, saveActivity, tracker.stop]);

  useEffect(() => {
    if (stage !== "starting" && stage !== "calibrating") return;
    if (["loading", "face-lost", "calibrating", "comfort-calibrating", "ready"].includes(tracker.status) && !permissionTracked.current) {
      permissionTracked.current = true;
      void trackEvent("camera_permission_outcome", { outcome: "granted" });
    }
    if (["calibrating", "comfort-calibrating"].includes(tracker.status)) setStage("calibrating");
    if (tracker.status === "ready") {
      setCountdown(3);
      setStage("countdown");
      void trackEvent("calibration_completed", { mode: "camera" });
    } else if (tracker.status === "error" && !permissionTracked.current) {
      permissionTracked.current = true;
      void trackEvent("camera_permission_outcome", { outcome: "unavailable" });
    }
  }, [stage, tracker.status]);

  useEffect(() => {
    if (stage !== "countdown") return;
    if (countdown <= 0) {
      startedAt.current = performance.now();
      activity.current = beginActivity();
      setStage("playing");
      void trackEvent("game_started", { game, mode });
      return;
    }
    const timer = window.setTimeout(() => setCountdown((value) => value - 1), 700);
    return () => window.clearTimeout(timer);
  }, [countdown, game, mode, stage]);

  useEffect(() => {
    if (stage !== "playing") return;
    const timer = window.setInterval(() => setElapsed(Math.floor((performance.now() - startedAt.current) / 1_000)), 1_000);
    return () => window.clearInterval(timer);
  }, [stage]);

  useEffect(() => {
    const interrupted = () => saveActivity("stopped");
    window.addEventListener("pagehide", interrupted);
    return () => {
      window.removeEventListener("pagehide", interrupted);
      interrupted();
    };
  }, [saveActivity]);

  const updateManualPoint = useCallback((clientX: number, clientY: number, bounds: DOMRect) => {
    setManualPoint(constrainPointToOrbit({
      x: ((clientX - bounds.left) / bounds.width) * 2 - 1,
      y: ((clientY - bounds.top) / bounds.height) * 2 - 1,
    }));
  }, []);

  const moveManualByKey = useCallback((key: string) => {
    const delta = 0.12;
    setManualPoint((point) => {
      const changes: Record<string, CalibratedPoint> = {
        ArrowLeft: { x: point.x - delta, y: point.y },
        ArrowRight: { x: point.x + delta, y: point.y },
        ArrowUp: { x: point.x, y: point.y - delta },
        ArrowDown: { x: point.x, y: point.y + delta },
      };
      return changes[key] ? constrainPointToOrbit(changes[key]) : point;
    });
  }, []);

  const trackedPoint = playablePoint(tracker.input);
  return {
    ...tracker,
    stage, mode, countdown, elapsed, manualPoint,
    point: mode === "manual" ? manualPoint : trackedPoint,
    cursor: mode === "manual" ? manualPoint : tracker.input ? { x: tracker.input.x, y: tracker.input.y } : { x: 0, y: 0 },
    startCamera, startManual, reset, complete, updateManualPoint, moveManualByKey,
  };
}

export type GentleGameRuntime = ReturnType<typeof useGentleGame>;
